import React, { ReactElement } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { ChoiceGroup } from '@apps/card/routes/CardCustomizer';
import { StationeryProductSelector } from '../ProductDetailsPage.types';
import { useDisplayPrice } from '../useDisplayPrice';
import { useSaleColor } from '../../DesignsGallery/utils/useSaleColor';
import { useProductToUIMapping } from '@apps/card/components/ProductUIConfig/useProductToUIMapping';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useGetDiscountByVendor } from '../../DesignsGallery/components/GalleryItem/hooks/useGetDiscountByVendor';
import { StationeryProduct } from '@shared/utils/print-price/types';
import { Product } from '@apps/card/components/ProductUIConfig/product.types';
import { useTranslation } from '@shared/core';

const DiscountedPriceText = ({ originalPrice, newPrice }: { originalPrice: string; newPrice: string }) => {
  const saleColor = useSaleColor();
  const { tq } = useTranslation('stationery');
  return (
    <TextV2>
      <TextV2
        as="span"
        typographyVariant="body3"
      >
        {tq`From`}
      </TextV2>{' '}
      <TextV2
        as="span"
        textDecoration="line-through"
        color="mono10"
        typographyVariant="body3"
      >
        {originalPrice}
      </TextV2>{' '}
      <TextV2
        as="span"
        color={saleColor}
        fontWeight={600 as const}
        typographyVariant="body3"
      >
        {newPrice}
      </TextV2>
    </TextV2>
  );
};

const ActionItem = ({ selected, title, price }: { selected: boolean; title: string; price?: string | ReactElement }) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      flex={1}
      paddingX={3}
    >
      <TextV2
        typographyVariant="body1"
        color="mono12"
        textTransform="capitalize"
        fontWeight={selected ? 600 : 400}
      >
        {title}
      </TextV2>
      {typeof price === 'string' ? (
        <TextV2
          typographyVariant="body3"
          color="mono12"
          fontWeight={selected ? 600 : 400}
        >
          {price}
        </TextV2>
      ) : (
        price
      )}
    </Flex>
  );
};

const isProduct = (input: unknown): input is StationeryProduct & Product => {
  return !!input;
};

export const ProductSelector = (props: { productSelector: StationeryProductSelector; pricing: ReturnType<typeof useDisplayPrice> }) => {
  const { productSelector } = props;
  const { getProductWithUIMapping } = useProductToUIMapping();
  const { products: productInfo, activeProduct, priceCalculator } = useStationeryProductPricingContext();
  const products = productInfo.map(p => getProductWithUIMapping(p)).filter(isProduct);
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const { getDiscountByVendor } = useGetDiscountByVendor();
  const { tq } = useTranslation('stationery');

  if (products.length === 0) {
    return null;
  }

  return (
    <Box marginBottom={7}>
      <ChoiceGroup
        title={tq`Product`}
        getName={() => (products.length === 1 ? activeProduct.translations.formatName : '')}
        value={productSelector.productId}
        minHeight={55}
        choices={products.map(p => p.id)}
        setValue={productSelector.onChange}
        columns={1}
      >
        {products.map((product, index) => {
          const discount = getDiscountByVendor(product.vendor);
          const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, discount, true);
          const priceDataDiscounts = priceData.discounts;
          const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
          const preDiscountUnitPriceString = priceDataDiscounts && minorUnitsToPriceString(priceDataDiscounts.preDiscountedUnitPriceInMinorUnits, { canShowFreeLabel: true });

          const isDiscountDifferent = preDiscountUnitPriceString !== unitPriceString;
          const canDisplayDiscount = discount && preDiscountUnitPriceString && isDiscountDifferent;

          const priceDisplay = (() => {
            if (canDisplayDiscount) {
              return (
                <DiscountedPriceText
                  originalPrice={preDiscountUnitPriceString}
                  newPrice={unitPriceString}
                />
              );
            }

            return priceData.unitPriceInMinorUnits === 0
              ? unitPriceString // Expected to be "FREE" or a translation of it.
              : tq`From ${unitPriceString}`;
          })();

          return (
            <ActionItem
              key={index}
              selected={product.id === activeProduct.id}
              title={product.translations.displayName}
              price={priceDisplay}
            />
          );
        })}
      </ChoiceGroup>
    </Box>
  );
};
